import { ErrorMessage } from "@hookform/error-message";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";
import { camelCase, isEmpty, has } from "lodash";

import UserContent from "~/components/UserContent";
import ErrorBoundary from "~/components/ErrorBoundary";
import { Error } from "./Input";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { getLegend, getErrorMessage } from "~/utilities/formHelpers";

const OptionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const CheckboxLabel = styled.label`
    display: flex;
    gap: 1.5rem;
    align-items: center;
    font-size: 1.8rem;
    color: ${({ theme }) => theme.palette.black};
    cursor: ${({ disabled }) => (disabled ? `not-allowed` : "pointer")};
    max-width: max-content;

    &:focus,
    &:focus-within {
        outline: 3px solid ${({ theme }) => theme.palette.focusBlue};
        outline-offset: 5px;
    }
`;

const Input = styled.input`
    opacity: 0;
    height: 0;
    width: 0;
    position: absolute;

    &:checked + span:after {
        display: block;
    }

    &:checked ~ span {
        background-color: ${({ theme }) => theme.palette.grayOne};
    }
`;

const Checkmark = styled.span`
    position: relative;
    border: 1px solid
        ${({ theme, error }) =>
            error ? theme.palette.red : theme.palette.grayTwo};
    background-color: ${({ theme }) => theme.palette.grayThree};
    width: 22px;
    height: 22px;
    display: inline-block;
    flex-shrink: 0;

    &::after {
        content: "";
        display: none;
        position: absolute;
        top: 30%;
        left: 30%;
        width: 5px;
        height: 12px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg) translate(-30%, -30%);
    }
`;

const Checkbox = ({
    label,
    name,
    register,
    options,
    errors = null,
    required = false,
    disabled = false,
    labelPlacement = "",
    errorMessage = "",
}) => {
    if (isEmpty(options)) {
        return null;
    }

    return (
        <ErrorBoundary>
            <fieldset
                aria-labelledby={`${camelCase(label)}FieldLabel`}
                aria-describedby={
                    has(errors, name) ? `${name}-field-error` : ""
                }
            >
                {getLegend(labelPlacement, required, label)}

                <OptionsWrapper>
                    {options.map(({ text, value }, index) => (
                        <CheckboxLabel
                            key={index}
                            disabled={disabled}
                            htmlFor={`${name}_${index}`}
                        >
                            <Input
                                {...{ disabled, name, value }}
                                {...register(name, {
                                    required: required
                                        ? getErrorMessage(errorMessage, label)
                                        : false,
                                })}
                                type="checkbox"
                                id={`${name}_${index}`}
                            />

                            <Checkmark error={has(errors, name)} />

                            <UserContent>
                                {required &&
                                labelPlacement === "hidden_label" ? (
                                    <span aria-hidden="true">*</span>
                                ) : (
                                    ""
                                )}
                                {sanitizeAndParseHTML(text)}
                            </UserContent>
                        </CheckboxLabel>
                    ))}
                </OptionsWrapper>

                <ErrorMessage
                    {...{ errors, name }}
                    render={({ message }) => (
                        <Error id={`${name}-field-error`}>{message}</Error>
                    )}
                />
            </fieldset>
        </ErrorBoundary>
    );
};

Checkbox.propTypes = {
    errors: PropTypes.shape({}),
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    labelPlacement: PropTypes.string,
    errorMessage: PropTypes.string,
};

export default Checkbox;
